/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-09-26 11:39:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import { Page, Layout, Card, SkeletonBodyText, ResourceList, ResourceItem } from '@shopify/polaris';
import history from 'src/routes/history';
import { routes } from 'src/routes';

import MainMenu from 'src/components/containers/main-menu';
import BannerNotify from 'src/components/views/recommendation/widget/management/banner-notify';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';
import FiltersBar from 'src/components/views/recommendation/widget/management/filters-bar.jsx';
import ActionGroup from 'src/components/views/recommendation/widget/management/action-group';
import ListWidgets from 'src/components/views/recommendation/widget/management/list-widgets/index.jsx';

import NoWidgetFound from 'src/components/views/recommendation/widget/management/list-widgets/no-widget-found';
import GrantPermissionsNotif from 'src/components/views/recommendation/grant-permissions-notif';
import DidYouKnow from 'src/components/views/recommendation/widget/management/did-you-know';
import AddonBanner from 'src/components/views/recommendation/widget/management/addon-banner';
import PromoteBanner from 'src/components/views/common/promote-banner';

import { URL } from 'src/constants/links';
import { getShop, buildUpdateAppPermissionURL } from 'src/helpers';
import { checkShopifyTestPlan } from 'src/helpers/subscription-v3-helper';
import { hasPermissionRecommendation } from 'src/helpers/permission-helper';
import {
  AIML_QUERY_INTERVAL_MILIS as INTERVAL_QUERY,
  RECOMMENDATION_WIDGET_STATUS
} from 'src/constants/recommendation';
import { ADDONS_INFO, AI_POWERED_RECOMMENDATION_NAME } from 'src/constants/subscription';
import ProcessAddon from 'src/components/views/subscription-v3/addon/process-addon';

import * as recommendationAction from 'src/stores/actions/recommendation-actions';
import * as subscriptionV3Actions from 'src/stores/actions/subscription-v3-actions';
import * as themeSetupAction from 'actions/theme-setup-actions';
import * as quickStartGuideAction from 'actions/dashboard/quick-start-guide-action';

import type { ComposedProps, State } from './index.type';
import TitleHelp from 'src/components/views/common/title-help';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import queryString from 'query-string';

class Widgets extends Component<ComposedProps, State> {
  querySyncOrderInterval = undefined;

  state = {
    queryValue: '',
    allWidgets: {},
    isSearch: false,
    isFilter: false,
    activateAITrain: false,
    modalAddon: false,

    activePermissionNotif: false,
    aimlPermission: false,
    permissionURL: '',
    youtubeModal: false,
    filterOptions: {},
    loadThemesOption: false,
    selectedTab: 0,
    syncWidgetsFlag: 0
  };

  async componentDidMount() {
    const {
      getRecommendationWidgets,
      syncOrderStatus,
      getSyncOrderStatus,
      getAddonStatus,
      invoice,
      getDetailInvoice,
      getThemes,
      themes
    } = this.props;

    const queries = queryString.parse(window.location.search);
    let { tab: selectedTab } = queries;

    const tab = +selectedTab || _.get(this.props, 'location.state.selectedTab', 0);
    this.setState(
      {
        selectedTab: tab
      },
      () => {
        this.handleChangeTab(tab);
      }
    );
    // Show Grant permission notification
    this.showGrantPermissionsModalNotif();

    // Get list theme for filter
    if (_.isEmpty(themes)) {
      await Promise.all([getThemes(), getRecommendationWidgets()]);
    } else {
      await getRecommendationWidgets();
      this.setLoadThemesOption(true);
    }

    this.pushRecommendationWidgetsToList();

    getAddonStatus(AI_POWERED_RECOMMENDATION_NAME);
    // Get detail invoice
    if (_.isEmpty(invoice) || !invoice) {
      getDetailInvoice();
    }

    const syncOrderCurrentStatus = _.get(syncOrderStatus, 'current', {});
    if (syncOrderCurrentStatus.status !== 'done') getSyncOrderStatus();
  }

  async componentWillReceiveProps(nextProps) {
    const { aimlPermission, activateAITrain } = this.state;
    const { loading: prevLoading, activateAIBase, getSyncOrderStatus } = this.props;
    const {
      loading,
      success,
      syncOrderStatus,
      userAppVersion,
      hasAddonAIPowerRecommendation,
      isCompletedTour,
      recommendationWidgets
    } = nextProps;

    const syncOrderCurrentStatus = _.get(syncOrderStatus, 'current', {});

    if (aimlPermission) {
      // if new user in app v3 => user went through KYC flow
      if (userAppVersion !== 'v3' && activateAITrain) {
        activateAIBase();
        this.setState({ activateAITrain: false });
      }

      if (!this.querySyncOrderInterval && syncOrderCurrentStatus.status !== 'done') {
        //  Sync order status is not done => interval query until done
        getSyncOrderStatus();
        this.querySyncOrderInterval = setInterval(() => {
          getSyncOrderStatus();
        }, INTERVAL_QUERY);
      }

      // if Sync order status is done => clear interval query
      else if (syncOrderCurrentStatus.status === 'done') {
        clearInterval(this.querySyncOrderInterval);
      }

      // if sync order status is done
      if (
        syncOrderCurrentStatus.status === 'done' &&
        syncOrderCurrentStatus.days === 90 &&
        hasAddonAIPowerRecommendation
      ) {
        // clear interval sync query
        if (this.querySyncOrderInterval) clearInterval(this.querySyncOrderInterval);
      }
    }

    if (prevLoading === true && loading === false && success === true) {
      const hasWidgetDraft = recommendationWidgets.find(e => e.widgetStatus === 'draft');

      // Set default selected tab
      if (isCompletedTour === false && hasWidgetDraft) {
        this.setState({ selectedTab: 1 });
      }
    }
  }

  componentWillUnmount() {
    if (this.querySyncOrderInterval) clearInterval(this.querySyncOrderInterval);
  }

  render() {
    const {
      i18n,
      loading,
      planName,
      tenantId,
      deleteRecommendationWidget,
      syncOrderStatus,
      hasAddonAIPowerRecommendation,
      statusStore,
      addonStatus,
      themes,
      getThemesLoading,
      widgetsSyncing
    } = this.props;
    const {
      queryValue,
      allWidgets,
      permissionURL,
      activePermissionNotif,
      isSearch,
      isFilter,
      aimlPermission,
      youtubeModal,
      filterOptions,
      loadThemesOption,
      selectedTab,
      syncWidgetsFlag
    } = this.state;
    const currentTab = RECOMMENDATION_WIDGET_STATUS[selectedTab].value;
    const widgetsOnTab = allWidgets[currentTab] || [];
    // Get flag addon ai power recommendation here
    const addonRemainingCycles = _.get(addonStatus, 'trialInfo.remainingDays', 14);
    const isSubscribeNow =
      !['trial', 'expiredSoon'].includes(statusStore) && addonRemainingCycles <= 0;
    const disableLockUnLock =
      (checkShopifyTestPlan(planName, tenantId) &&
        ['trial', 'expiredSoon'].includes(statusStore)) ||
      statusStore === 'expired';

    const syncOrderPreviousStatus = _.get(syncOrderStatus, 'previous', {});
    const syncOrderCurrentStatus = _.get(syncOrderStatus, 'current', {});

    // Auto trigger sync order data
    const showSyncOrderStatus =
      aimlPermission &&
      syncOrderCurrentStatus.status === 'running' &&
      (syncOrderCurrentStatus.days === 30 || syncOrderCurrentStatus.days === 90);

    return (
      <div className="BC-Page BC-Page__RecommendationWidgetManagement">
        <MainMenu menuId="menu-recommendation-widget" />
        <PromoteBanner
          name="RecommendationWidgetHighlightInfo"
          feature="Recommendation"
          learnMoreLink="RecommendationHandPickedSettingsLearnMore"
        />
        <Page
          title={i18n.translate('AdminHTML.Recommendation.WidgetDashboard.title')}
          primaryAction={
            <div className="BC-Page BC-Page__Button">
              <ActionGroup />
            </div>
          }>
          <TitleHelp
            i18n={i18n}
            onPlay={() => this.toggleModal('youtubeModal')}
            hrefUserManual={URL['Link_V2_Recommendation_ManageWidget_ManualGuide']}
            hrefBestPractice={URL['Link_V2_Recommendation_ManageWidget_BestPractice']}
            openStepGuide={this.openStepGuide}
          />
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Installation reminder status banner */}
                <InstallationRemindStatusBanner />

                {/* Introduce banner */}
                <DidYouKnow />

                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />
              </div>
            </Layout.Section>

            {/* Update permission recommendation*/}
            {activePermissionNotif && (
              <Layout.Section>
                <GrantPermissionsNotif permissionURL={permissionURL} />
              </Layout.Section>
            )}

            {/* Show sync order status banner for new user use app v3 */}
            {showSyncOrderStatus && (
              <Layout.Section>
                <BannerNotify
                  title={i18n.translate('AdminHTML.Recommendation.SyncBanner.bannerTitle')}
                  contents={[
                    i18n.translate(
                      `AdminHTML.Recommendation.SyncBanner.${
                        syncOrderCurrentStatus.days === 30
                          ? 'syncOrder30'
                          : syncOrderPreviousStatus.days === 30
                          ? 'syncOrder90_second'
                          : 'syncOrder90_first'
                      }`
                    )
                  ]}
                  dismissName={'syncOrderStatusBanner'}
                />
              </Layout.Section>
            )}

            {/* List of widgets */}
            <Layout.Section>
              <div className="BC-Management-Widget">
                <Card>
                  <FiltersBar
                    queryValue={queryValue}
                    filterOptions={filterOptions}
                    themes={themes}
                    getThemesLoading={getThemesLoading}
                    onQueryChange={this.handleFiltersQueryChange}
                    handleKeyDown={this.handleKeyDown}
                    handleKeyUp={this.handleKeyUp}
                    handleFilter={this.handleFilter}
                    loadThemesOption={loadThemesOption}
                    setLoadThemesOption={this.setLoadThemesOption}
                    selectedTab={selectedTab}
                    handleChangeTab={this.handleChangeTab}
                    syncWidgets={this.syncWidgets}
                    syncWidgetsFlag={syncWidgetsFlag}
                  />
                  {loading || widgetsSyncing ? (
                    <ResourceList
                      resourceName={{ singular: 'widget', plural: 'widgets' }}
                      items={[...Array(3).keys()]}
                      renderItem={item => {
                        return (
                          <ResourceItem id={item}>
                            <SkeletonBodyText lines={2} />
                          </ResourceItem>
                        );
                      }}
                    />
                  ) : widgetsOnTab.length === 0 ? (
                    /* Incase not widget found when search */
                    <NoWidgetFound
                      isSearch={isSearch}
                      isFilter={isFilter}
                      selectedTab={selectedTab}
                    />
                  ) : (
                    <ListWidgets
                      isApplyFilter={isSearch || isFilter}
                      allWidgets={widgetsOnTab}
                      deleteRecommendationWidget={deleteRecommendationWidget}
                      afterDeleteWidget={this.afterDeleteWidget}
                      updateWidgetAction={this.updateWidgetAction}
                      duplicateWidget={this.duplicateWidget}
                      hasAddonAIPowerRecommendation={hasAddonAIPowerRecommendation}
                      selectedTab={selectedTab}
                      handleUnlock={() => this.toggleModal('modalAddon')}
                    />
                  )}
                </Card>
              </div>
            </Layout.Section>
          </Layout>
          <div className="Polaris-PageActions">
            <div className="Polaris-Stack Polaris-Stack--spacingTight Polaris-Stack--distributionTrailing">
              <ActionGroup />
            </div>
          </div>
          <AddonBanner
            hasAddonAIPowerRecommendation={hasAddonAIPowerRecommendation}
            handleUnlock={() => {
              if (disableLockUnLock) return;
              this.toggleModal('modalAddon');
            }}
            isSubscribeNow={isSubscribeNow}
            disableLockUnLock={disableLockUnLock}
          />
        </Page>
        {/* Unlock addon to using feature */}
        <ProcessAddon
          openModalReview={this.state.modalAddon}
          addon={ADDONS_INFO.find(e => e.addonName === AI_POWERED_RECOMMENDATION_NAME)}
          hadSubscribed={isSubscribeNow}
          handleAfterSubscribeAddon={this.handleCloseStatus}
          handleReviewModal={() => this.toggleModal('modalAddon')}
        />
        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['Link_V2_Recommendation_ManageWidget_Video']}
          onClose={() => this.toggleModal('youtubeModal')}
          title={i18n.translate('AdminHTML.Analytics.Analytics.youtubeModal_Title')}
        />
      </div>
    );
  }

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  handleCloseStatus = () => {
    this.props.getAddonStatus(AI_POWERED_RECOMMENDATION_NAME);
  };

  // Push widgets to list every time after reading from DB
  pushRecommendationWidgetsToList = (filteredWidgets = []) => {
    const { isSearch, isFilter } = this.state;
    const { recommendationWidgets = [] } = this.props;
    let widgets = [];
    // Merge new widgets
    if (isSearch || isFilter) {
      widgets = filteredWidgets;
    } else {
      widgets = recommendationWidgets;
    }

    this.setState({ allWidgets: _.groupBy(widgets, item => item.widgetStatus) });
  };

  // Post process after delete widget
  afterDeleteWidget = async widgetId => {
    let { allWidgets, selectedTab } = this.state;
    // await this.props.getRecommendationWidgets();
    const currentTab = RECOMMENDATION_WIDGET_STATUS[selectedTab].value;
    const newData = _.reject(allWidgets[currentTab], {
      id: widgetId
    });
    this.setState({ allWidgets: { ...allWidgets, [currentTab]: newData } });
  };

  // On search text change
  handleFiltersQueryChange = queryValue => {
    this.setState({ queryValue });
  };

  // Key press event on search field
  handleKeyDown = event => {
    clearTimeout(this.timerTyping);
  };

  // Key up event, start searching after 500ms
  handleKeyUp = event => {
    // Invoke search request after 500ms
    this.timerTyping = setTimeout(() => {
      this.handleFilterAndSearch();
    }, 500);
  };

  // search widgets by query value
  searchWidget = widgets => {
    let queryValue = this.state.queryValue.toLowerCase().trim();
    let filteredWidgets = widgets;
    // Having search text, search widgets
    if (queryValue) {
      // Enable flag search
      this.setState({ isSearch: true });
      filteredWidgets = widgets.filter(widget => {
        const widgetName = _.get(widget, 'widgetName', '');
        const widgetId = _.get(widget, 'id', '');
        return (
          widgetName.toLowerCase().includes(queryValue) ||
          widgetId.toLowerCase().includes(queryValue)
        );
      });
    } else {
      this.setState({ isSearch: false });
    }
    return filteredWidgets;
  };

  // Filter widget
  filterWidget = (widgets, filterOptions) => {
    const { themeOption, recommendationTypeOption } = filterOptions;
    let filteredWidgets = widgets;
    if (_.isEmpty(themeOption) && _.isEmpty(recommendationTypeOption)) {
      this.setState({ isFilter: false });
    } else {
      this.setState({ isFilter: true });
      if (!_.isEmpty(themeOption)) {
        filteredWidgets = _.filter(
          filteredWidgets,
          widget =>
            !_.isEmpty(
              _.find(widget.displayThemes, theme => theme.themeId.toString() === themeOption)
            )
        );
      }
      if (!_.isEmpty(recommendationTypeOption)) {
        filteredWidgets = _.filter(
          filteredWidgets,
          widget => widget.recommendationType === recommendationTypeOption
        );
      }
    }
    return filteredWidgets;
  };

  // Handle search
  handleFilterAndSearch = async () => {
    const { recommendationWidgets } = this.props;
    const { filterOptions } = this.state;
    // Apply search
    let filteredWidgets = this.searchWidget(recommendationWidgets);
    // Apply filter
    filteredWidgets = this.filterWidget(filteredWidgets, filterOptions);

    this.pushRecommendationWidgetsToList(filteredWidgets);
  };

  // Handle filter
  handleFilter = async filterOptions => {
    await this.setState({ filterOptions: filterOptions });
    this.handleFilterAndSearch();
  };

  // Show notification
  showGrantPermissionsModalNotif = () => {
    const { accessScopes } = this.props;

    // Remove localStorage
    try {
      const keyRedirect = `${getShop()}-permissionApproval-redirectURI`;
      const valueRedirect = localStorage.getItem(keyRedirect);
      if (valueRedirect === '/recommendation-widgets') this.setState({ activateAITrain: true });
      localStorage.removeItem(keyRedirect);
    } catch (error) {}

    // Check permission from access scopes' tenant app
    if (hasPermissionRecommendation(accessScopes)) {
      this.setState({ aimlPermission: true });
      return;
    }

    // Get update permission link
    const permissionURL = buildUpdateAppPermissionURL(getShop());

    this.setState({ activePermissionNotif: true, permissionURL });
  };

  getAIMLStatusContent = status => {
    if (status === 'DONE') return 'doneContent';
    if (status === 'NOT_ENOUGH') return 'notEnoughContent';
    return 'processingContent';
  };

  setLoadThemesOption = (loadThemesOption: boolean) => {
    this.setState({ loadThemesOption });
  };

  openStepGuide = () => {
    history.push(routes.recommendationWidgets.guide());
  };

  handleChangeTab = (selected: number) => {
    this.setState({ selectedTab: selected });
  };

  // Handle refresh widgets action
  syncWidgets = () => {
    const { syncWidgets } = this.props;
    const meta = {
      onSuccess: () => {
        const { syncWidgetsFlag } = this.state;
        // Reset filter and search
        this.setState({
          queryValue: '',
          isSearch: false,
          isFilter: false,
          filterOptions: {},
          syncWidgetsFlag: 1 - syncWidgetsFlag
        });
        // Update widget list with the new ones
        this.pushRecommendationWidgetsToList();
      }
    };
    syncWidgets(meta);
  };

  // Handle action on widget: launch, pause, archive, restore
  updateWidgetAction = (widgetId, actionType) => {
    const { updateWidgetAction, markDoneStep } = this.props;
    const meta = {
      onSuccess: () => {
        this.afterUpdateAction(widgetId, actionType);
      }
    };

    if (actionType === 'launch') {
      markDoneStep('V2_RECOMMEND1', {
        isDone: true
      });
    }
    updateWidgetAction(widgetId, actionType, meta);
  };

  afterUpdateAction = (widgetId, actionType) => {
    const { allWidgets } = this.state;
    const newAllWidgets = _.cloneDeep(allWidgets);
    const { live = [], archive = [], draft = [] } = newAllWidgets;

    switch (actionType) {
      case 'restore': {
        const widget = _.remove(archive, item => item.id === widgetId);
        draft.push(widget[0]);
        break;
      }
      case 'archive': {
        const widget = _.remove(draft, item => item.id === widgetId);
        archive.push(widget[0]);
        break;
      }
      case 'pause': {
        const widget = _.remove(live, item => item.id === widgetId);
        draft.push(widget[0]);
        break;
      }
      case 'launch': {
        const { actionURL } = this.props;
        window.open(actionURL, '_blank');
        break;
      }
      default:
    }
    if (actionType !== 'launch') {
      this.setState({ allWidgets: { ...newAllWidgets, live, draft, archive } });
    }
  };

  duplicateWidget = widgetId => {
    const { duplicateWidget } = this.props;
    const meta = {
      onSuccess: () => {
        this.afterDuplicateWidget();
      }
    };
    duplicateWidget(widgetId, meta);
  };

  afterDuplicateWidget = () => {
    const { selectedTab, allWidgets } = this.state;
    const { draft = [] } = allWidgets;

    // Add duplicate to draft
    const { recommendationWidget } = this.props;
    if (!_.isEmpty(recommendationWidget)) {
      draft.push(recommendationWidget);
      this.setState({ ...allWidgets, draft });
    }

    if (selectedTab !== 1) {
      this.setState({ selectedTab: 1 });
    }
    // Redirect to edit page
    // const { widgetId } = this.props;
    // if (_.isEmpty(widgetId)) {
    //   history.push(routes.recommendationWidgets.view(widgetId));
    // }
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Get current status addon sync
  getAddonStatus: addonName => dispatch(subscriptionV3Actions.getAddonStatus(addonName)),
  getDetailInvoice: () => dispatch(subscriptionV3Actions.getDetailInvoice(ownProps.i18n)),
  // Get recommendation widgets
  getRecommendationWidgets: () =>
    dispatch(recommendationAction.getRecommendationWidgets(ownProps.i18n)),

  deleteRecommendationWidget: widgetId =>
    dispatch(recommendationAction.deleteRecommendationWidget(widgetId, ownProps.i18n)),
  activateAIBase: () => dispatch(recommendationAction.activateAIBase(ownProps.i18n)),
  getSyncOrderStatus: () => dispatch(recommendationAction.getSyncOrderStatus(ownProps.i18n)),
  syncWidgets: meta => dispatch(recommendationAction.syncWidgets(meta, ownProps.i18n)),
  updateWidgetAction: (widgetId, actionType, meta) =>
    dispatch(
      recommendationAction.updateRecommendationWidgetAction(
        widgetId,
        actionType,
        meta,
        ownProps.i18n
      )
    ),
  duplicateWidget: (widgetId, meta) =>
    dispatch(recommendationAction.duplicateRecommendationWidget(widgetId, meta, ownProps.i18n)),
  getThemes: () => dispatch(themeSetupAction.getThemes(ownProps.i18n)),
  markDoneStep: (stepName, value) =>
    dispatch(quickStartGuideAction.markDoneStep(stepName, value, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  // Get widgets in case of editing
  const recommendationWidgets = _.get(state.recommendation, 'recommendationWidgets', []);
  const recommendationWidget = _.get(state.recommendation, 'recommendationWidget', []);

  // Get access scopes
  const accessScopes = _.get(state.oauth, 'tenantApp.accessScopes', {});
  const settings = _.get(state.oauth, 'tenantApp.settings', {});
  const userAppVersion = _.get(state.oauth, 'tenantApp.userAppVersion', '');
  const themes = _.get(state.themeSetup, 'themes', []);
  const getThemesLoading = _.get(state.themeSetup, 'getThemesLoading', false);
  const tenantId = _.get(state.oauth, 'tenantApp.tenantId', '');
  const planName = _.get(state.oauth, 'tenantApp.externalInfo.plan_name', '');
  return {
    invoice: _.get(state.subscriptionV3, 'invoice', {}),
    statusStore: _.get(state.oauth, 'subscription.status', ''),
    addonStatus: _.get(state.subscriptionV3, 'addonStatus', {}),
    hasAddonAIPowerRecommendation: _.get(
      state.oauth.tenantApp,
      'settings.recommendation.settings.frequently-bought-together',
      false
    ),
    saving: _.get(state.recommendation, 'saving', false),
    deleting: _.get(state.recommendation, 'deleting', false),
    success: _.get(state.recommendation, 'success', false),
    loading: _.get(state.recommendation, 'loading', false),
    aimlSyncStatus: _.get(state.recommendation, 'aimlSyncStatus', ''),
    syncOrderStatus: _.get(state.recommendation, 'syncOrderStatus', {}),
    widgetsSyncing: _.get(state.recommendation, 'widgetsSyncing', false),
    actionURL: _.get(state.recommendation, 'actionURL', ''),
    widgetId: _.get(state.recommendation, 'widgetId', ''),
    recommendationWidgets,
    recommendationWidget,
    accessScopes,
    settings,
    userAppVersion,
    planName,
    tenantId,
    isCompletedTour: _.get(
      state.oauth.tenantApp,
      'settings.recommendation.settings.isCompletedTour',
      null
    ),
    themes,
    getThemesLoading
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Widgets));
